import { useCallback, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useStoreHome } from "../Home/useStoreHome";
import { obtenerContextoSesion } from "../../servicios/contextoSesion";
import { listarPuntosVenta } from "../../servicios/puntosVentaApi";
import {
  configurarFormaPagoCaja,
  crearCaja,
  listarCajas,
  listarFormasPagoCaja,
  modificarCaja,
  obtenerCaja,
  obtenerHistorialCajas,
  obtenerResumenCaja,
} from "../../servicios/cajasApi";
import {
  crearRequestGuardarCaja,
  crearUuidCaja,
  enriquecerHistorialOperadores,
  extraerCajas,
  extraerFormasPagoCaja,
  extraerHistorialCajas,
  fechaCajaParaInput,
  formatearFechaCaja,
  importeMovimientoVisual,
  normalizarCaja,
  normalizarResumenCaja,
  puedeAdministrarCajas,
  respuestaCaja,
} from "../../servicios/cajas";
import { useStoreCajas } from "./useStoreCajas";
import CajaAperturaModal from "./CajaAperturaModal";
import CajaOperadorSelector from "./CajaOperadorSelector";
import "./cajas.css";

const dinero = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" });
const inicial = (empresaId) => ({ id: 0, empresaId, codigo: "", descripcion: "", modo: "Compartida", pcId: null, puntoVentaId: null, gestionAutomatica: true, esPredeterminada: false, activo: true });
const contexto = () => obtenerContextoSesion({ EmpresaId: useStoreHome.getState().EmpresaId });
const extraerLista = (respuesta) => respuesta.data?.lista || respuesta.data?.Lista || [];
const errorDe = (respuesta) => respuestaCaja(respuesta).error;

const TABS = [
  ["sesion", "Sesión", "pi-wallet"],
  ["administracion", "Cajas", "pi-desktop"],
  ["historial", "Historial", "pi-history"],
  ["formas", "Formas de pago", "pi-credit-card"],
];

function Estado({ activo, children }) {
  return <span className={`cajas-estado ${activo ? "exito" : "neutro"}`}>{children}</span>;
}

function EstadoVacio({ icono = "pi-inbox", titulo, descripcion, children }) {
  return (
    <div className="cajas-vacio">
      <span className="cajas-vacio-icono"><i className={`pi ${icono}`} aria-hidden="true" /></span>
      <h2>{titulo}</h2>
      <p>{descripcion}</p>
      {children && <div className="cajas-vacio-acciones">{children}</div>}
    </div>
  );
}

export default function Cajas({ modoReporte = false }) {
  const [tab, setTab] = useState("sesion");
  const [lista, setLista] = useState([]);
  const [puntos, setPuntos] = useState([]);
  const [modal, setModal] = useState(false);
  const [item, setItem] = useState(inicial(contexto().EmpresaId));
  const [ocupado, setOcupado] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [historialConsultado, setHistorialConsultado] = useState(false);
  const [paginacionHistorial, setPaginacionHistorial] = useState({ pagina: 1, tamano: 25 });
  const [resumenModalAbierto, setResumenModalAbierto] = useState(false);
  const hoy = fechaCajaParaInput();
  const [filtros, setFiltros] = useState({ cajaId: "", fechaDesde: hoy, fechaHasta: hoy });
  const [detalle, setDetalle] = useState(null);
  const [formas, setFormas] = useState([]);
  const [mov, setMov] = useState({ tipo: "Ingreso", importe: "", concepto: "", claveIdempotencia: "" });
  const [cierre, setCierre] = useState({ saldoDeclarado: "", observacion: "" });

  const sesion = useStoreCajas((state) => state.sesionActiva);
  const resumen = useStoreCajas((state) => state.resumen);
  const error = useStoreCajas((state) => state.error);
  const aperturaRequerida = useStoreCajas((state) => state.aperturaRequerida);
  const movimientoEnCurso = useStoreCajas((state) => state.movimientoEnCurso);
  const cerrando = useStoreCajas((state) => state.cerrando);
  const operador = JSON.parse(localStorage.getItem("Operador") || "null");
  const permisos = JSON.parse(localStorage.getItem("Permisos") || "null");
  const admin = puedeAdministrarCajas({ operador, permisos });
  const ctx = contexto();

  const cargarCatalogos = useCallback(async () => {
    setOcupado(true);
    const [cajasRespuesta, puntosRespuesta, formasRespuesta] = await Promise.all([
      listarCajas({ EmpresaId: ctx.EmpresaId }),
      listarPuntosVenta({ EmpresaId: ctx.EmpresaId, activo: true }),
      listarFormasPagoCaja({ EmpresaId: ctx.EmpresaId }),
    ]);
    setLista(extraerCajas(cajasRespuesta));
    setPuntos(extraerLista(puntosRespuesta));
    setFormas(extraerFormasPagoCaja(formasRespuesta));
    setOcupado(false);
  }, [ctx.EmpresaId]);

  useEffect(() => {
    useStoreCajas.getState().resolverCaja({ mostrarSelector: false }).then(() => useStoreCajas.getState().actualizarResumen());
    cargarCatalogos();
  }, [cargarCatalogos]);

  const abrirEdicion = async (caja) => {
    if (!caja) {
      setItem(inicial(ctx.EmpresaId));
      setModal(true);
      return;
    }
    setOcupado(true);
    const respuesta = await obtenerCaja({ EmpresaId: ctx.EmpresaId, id: caja.id });
    setOcupado(false);
    if (errorDe(respuesta)) return Swal.fire("Error", errorDe(respuesta), "error");
    setItem(normalizarCaja(respuesta.data?.item ?? respuesta.data?.Item ?? respuesta.data));
    setModal(true);
  };

  const guardar = async (evento) => {
    evento.preventDefault();
    setOcupado(true);
    const request = crearRequestGuardarCaja({
      EmpresaId: ctx.EmpresaId,
      operadorId: ctx.operadorId,
      pcId: ctx.pcId,
      item: {
        ...item,
        id: Number(item.id) || 0,
        pcId: item.pcId ? Number(item.pcId) : null,
        puntoVentaId: item.puntoVentaId ? Number(item.puntoVentaId) : null,
      },
    });
    const respuesta = item.id ? await modificarCaja(request) : await crearCaja(request);
    setOcupado(false);
    if (errorDe(respuesta)) return Swal.fire("Error", errorDe(respuesta), "error");
    setModal(false);
    await cargarCatalogos();
  };

  const enviarMovimiento = async (evento) => {
    evento.preventDefault();
    if (Number(mov.importe) <= 0) return;
    const clave = mov.claveIdempotencia || crearUuidCaja();
    setMov((actual) => ({ ...actual, claveIdempotencia: clave }));
    const confirmacion = await Swal.fire({
      title: "Confirmar movimiento",
      text: `${mov.tipo} por ${dinero.format(Number(mov.importe))}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Confirmar",
    });
    if (!confirmacion.isConfirmed) return;
    const ok = await useStoreCajas.getState().registrarMovimiento({ ...mov, importe: Number(mov.importe), claveIdempotencia: clave });
    if (ok) setMov({ tipo: "Ingreso", importe: "", concepto: "", claveIdempotencia: "" });
  };

  const cerrar = async (evento) => {
    evento.preventDefault();
    await useStoreCajas.getState().actualizarResumen();
    const saldo = Number(useStoreCajas.getState().resumen?.saldoSistema) || 0;
    const declarado = Number(cierre.saldoDeclarado);
    const confirmacion = await Swal.fire({
      title: "Confirmar cierre de caja",
      html: `Saldo esperado: <b>${dinero.format(saldo)}</b><br>Declarado: <b>${dinero.format(declarado)}</b><br>Diferencia: <b>${dinero.format(declarado - saldo)}</b>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cerrar caja",
    });
    if (confirmacion.isConfirmed) await useStoreCajas.getState().cerrarCaja(cierre);
  };

  const buscarHistorial = async (evento) => {
    evento?.preventDefault();
    setOcupado(true);
    setDetalle(null);
    setResumenModalAbierto(false);
    setPaginacionHistorial((actual) => ({ ...actual, pagina: 1 }));
    const respuesta = await obtenerHistorialCajas({
      EmpresaId: ctx.EmpresaId,
      cajaId: filtros.cajaId ? Number(filtros.cajaId) : null,
      fechaDesde: `${filtros.fechaDesde}T00:00:00`,
      fechaHasta: `${filtros.fechaHasta}T23:59:59`,
      incluirOperadores: true,
    });
    setHistorialConsultado(true);
    if (errorDe(respuesta)) {
      setOcupado(false);
      return Swal.fire("Error", errorDe(respuesta), "error");
    }
    const sesiones = extraerHistorialCajas(respuesta);
    const sesionesConOperadores = await enriquecerHistorialOperadores(
      sesiones,
      async (sesionHistorial) => {
        const respuestaResumen = await obtenerResumenCaja({
          EmpresaId: ctx.EmpresaId,
          cajaSesionId: sesionHistorial.id,
        });
        return errorDe(respuestaResumen) ? null : normalizarResumenCaja(respuestaResumen.data);
      },
    );
    setHistorial(sesionesConOperadores);
    setOcupado(false);
  };

  const verDetalle = async (id) => {
    setOcupado(true);
    const respuesta = await obtenerResumenCaja({ EmpresaId: ctx.EmpresaId, cajaSesionId: Number(id) });
    setOcupado(false);
    if (errorDe(respuesta)) return Swal.fire("Error", errorDe(respuesta), "error");
    setDetalle(normalizarResumenCaja(respuesta.data));
    setResumenModalAbierto(true);
  };

  const abrirResumenSesion = async () => {
    setDetalle(null);
    const datos = resumen || await useStoreCajas.getState().actualizarResumen();
    if (datos) setResumenModalAbierto(true);
  };

  const actualizarForma = async (forma, campo, valor) => {
    setOcupado(true);
    const respuesta = await configurarFormaPagoCaja({
      EmpresaId: ctx.EmpresaId,
      formaPagoId: forma.formaPagoId,
      [campo]: valor,
      impactaArqueo: campo === "impactaArqueo" ? valor : forma.impactaArqueo,
      activo: campo === "activo" ? valor : forma.activo,
      operadorId: ctx.operadorId,
      pcId: ctx.pcId,
    });
    setOcupado(false);
    if (errorDe(respuesta)) return Swal.fire("Error", errorDe(respuesta), "error");
    await cargarCatalogos();
  };

  const saldoSistema = Number(resumen?.saldoSistema ?? sesion?.saldoSistema) || 0;
  const diferencia = Number(cierre.saldoDeclarado || 0) - saldoSistema;
  const datosResumenModal = detalle || resumen;
  const movimientosModal = useMemo(() => datosResumenModal?.movimientos || [], [datosResumenModal]);
  const paginasHistorial = Math.max(1, Math.ceil(historial.length / paginacionHistorial.tamano));
  const paginaHistorial = Math.min(paginacionHistorial.pagina, paginasHistorial);
  const historialPaginado = useMemo(() => {
    const inicio = (paginaHistorial - 1) * paginacionHistorial.tamano;
    return historial.slice(inicio, inicio + paginacionHistorial.tamano);
  }, [historial, paginaHistorial, paginacionHistorial.tamano]);
  const tabsVisibles = modoReporte
    ? TABS.filter(([id]) => ["sesion", "historial"].includes(id))
    : TABS.filter(([id]) => admin || !["administracion", "formas"].includes(id));

  return (
    <main className={`cajas ${modoReporte ? "cajas-modo-reporte" : ""}`}>
      <header className="cajas-encabezado">
        <div className="cajas-encabezado-principal">
          <span className="cajas-icono" aria-hidden="true"><i className="pi pi-wallet" /></span>
          <div><h1>{modoReporte ? "Movimientos de caja" : "Configuración de caja"}</h1><p>{modoReporte ? "Consultá el estado, los totales y el historial registrado." : "Gestioná sesiones, arqueos y configuración avanzada."}</p></div>
        </div>
        <div className={`cajas-sesion-chip ${sesion ? "abierta" : "cerrada"}`}>
          <span className="cajas-sesion-punto" />
          <span><small>Estado actual</small><strong>{sesion ? `${sesion.cajaCodigo} · ${sesion.estado}` : "Sin caja abierta"}</strong></span>
        </div>
      </header>

      <nav className="cajas-tabs vista-entrada-lateral" aria-label="Secciones de cajas">
        {tabsVisibles.map(([id, label, icono]) => (
          <button type="button" key={id} className={tab === id ? "activo" : ""} aria-pressed={tab === id} onClick={() => { setTab(id); setDetalle(null); setResumenModalAbierto(false); }}>
            <i className={`pi ${icono}`} aria-hidden="true" /> {label}
          </button>
        ))}
      </nav>

      <div className="cajas-contenido vista-entrada-lateral">
        {error && <div className="cajas-error" role="alert"><i className="pi pi-exclamation-circle" /><span>{error}</span></div>}

        {tab === "sesion" && (
          <section className="cajas-panel">
            {!sesion ? (
              <EstadoVacio icono="pi-wallet" titulo="No hay una caja disponible" descripcion={modoReporte ? "Configurá una caja automática predeterminada para comenzar a registrar movimientos." : "Buscá una sesión compatible o elegí una caja para comenzar a operar."}>
                {!modoReporte && <><button type="button" className="secundario" onClick={() => useStoreCajas.getState().resolverCaja()}><i className="pi pi-sync" /> Buscar sesión</button>
                <button type="button" className="primario" onClick={() => useStoreCajas.setState({ aperturaRequerida: true })}><i className="pi pi-plus" /> Abrir caja</button></>}
              </EstadoVacio>
            ) : (
              <>
                <div className="cajas-panel-cabecera">
                  <div><span className="cajas-seccion-icono"><i className="pi pi-bolt" /></span><div><small>Sesión activa</small><h2>Resumen operativo</h2></div></div>
                  <button type="button" className="secundario compacto" onClick={() => useStoreCajas.getState().actualizarResumen()}><i className="pi pi-refresh" /> Actualizar</button>
                </div>
                <div className="cajas-resumen-grid cajas-resumen-principal">
                  <article><span className="cajas-metrica-icono"><i className="pi pi-desktop" /></span><div><span>Caja</span><strong>{sesion.cajaCodigo}</strong><small>{sesion.cajaDescripcion}</small></div></article>
                  <article><span className="cajas-metrica-icono"><i className="pi pi-users" /></span><div><span>Modo</span><strong>{sesion.modo}</strong><small>Operador #{ctx.operadorId}</small></div></article>
                  <article><span className="cajas-metrica-icono"><i className="pi pi-clock" /></span><div><span>Apertura</span><strong>{formatearFechaCaja(sesion.fechaApertura)}</strong><small>{dinero.format(Number(sesion.saldoInicial) || 0)} inicial</small></div></article>
                  <article className="destacada"><span className="cajas-metrica-icono"><i className="pi pi-dollar" /></span><div><span>Saldo esperado</span><strong>{dinero.format(saldoSistema)}</strong><small>Actualizado desde el sistema</small></div></article>
                </div>

                {!modoReporte && <div className="cajas-dos-columnas cajas-operaciones">
                  <form className="cajas-form" onSubmit={enviarMovimiento}>
                    <div className="cajas-form-titulo"><span><i className="pi pi-arrow-right-arrow-left" /></span><div><h2>Movimiento manual</h2><p>Registrá ingresos, egresos o ajustes en efectivo.</p></div></div>
                    <div className="cajas-form-grid">
                      <label><span>Tipo</span><select value={mov.tipo} onChange={(e) => setMov({ ...mov, tipo: e.target.value, claveIdempotencia: "" })}>{["Ingreso", "Egreso", "Retiro", "Ajuste"].map((tipo) => <option key={tipo}>{tipo}</option>)}</select></label>
                      <label><span>Importe</span><div className="cajas-input-moneda"><b>$</b><input required type="number" min="0.01" step="0.01" value={mov.importe} onChange={(e) => setMov({ ...mov, importe: e.target.value, claveIdempotencia: "" })} placeholder="0,00" /></div></label>
                      <label><span>Concepto</span><input required value={mov.concepto} onChange={(e) => setMov({ ...mov, concepto: e.target.value, claveIdempotencia: "" })} placeholder="Motivo del movimiento" /></label>
                    </div>
                    <div className="cajas-form-pie"><span>Se registra como efectivo e impacta en el arqueo.</span><button className="primario" disabled={movimientoEnCurso}><i className={`pi ${movimientoEnCurso ? "pi-spin pi-spinner" : "pi-check"}`} /> {movimientoEnCurso ? "Registrando..." : "Registrar"}</button></div>
                  </form>

                  <form className="cajas-form" onSubmit={cerrar}>
                    <div className="cajas-form-titulo"><span className="alerta"><i className="pi pi-lock" /></span><div><h2>Cierre y arqueo</h2><p>Compará el efectivo declarado con el sistema.</p></div></div>
                    <label><span>Saldo declarado</span><div className="cajas-input-moneda"><b>$</b><input required type="number" step="0.01" value={cierre.saldoDeclarado} onChange={(e) => setCierre({ ...cierre, saldoDeclarado: e.target.value })} placeholder="0,00" /></div></label>
                    <div className={`cajas-diferencia ${diferencia === 0 ? "coincide" : "difiere"}`}><span><small>Diferencia preliminar</small><strong>{diferencia === 0 ? "Sin diferencias" : "Revisar importe"}</strong></span><b>{dinero.format(diferencia)}</b></div>
                    <label><span>Observación</span><textarea value={cierre.observacion} onChange={(e) => setCierre({ ...cierre, observacion: e.target.value })} placeholder="Detalle opcional del cierre" /></label>
                    <div className="cajas-form-pie cierre"><span>Esta acción finaliza la sesión actual.</span><button className="peligro" disabled={cerrando}><i className={`pi ${cerrando ? "pi-spin pi-spinner" : "pi-lock"}`} /> {cerrando ? "Cerrando..." : "Cerrar caja"}</button></div>
                  </form>
                </div>}

                {!modoReporte && sesion.modo === "Compartida" && (
                  <div className="cajas-operadores">
                    <div><span className="cajas-seccion-icono"><i className="pi pi-users" /></span><div><h2>Operadores de caja compartida</h2><p>Buscá por nombre, apellido o correo.</p></div></div>
                    <CajaOperadorSelector
                      EmpresaId={ctx.EmpresaId}
                      cajaSesionId={Number(sesion.id)}
                      operadorAccionId={ctx.operadorId}
                      pcId={ctx.pcId}
                      habilitado={Number(sesion.id) > 0 && sesion.estado === "Abierta" && sesion.modo === "Compartida"}
                      onAgregado={() => useStoreCajas.getState().actualizarResumen()}
                    />
                  </div>
                )}
                <div className="cajas-resumen-acceso">
                  <div><span className="cajas-seccion-icono"><i className="pi pi-chart-bar" /></span><div><h2>Resumen de sesión y movimientos</h2><p>Consultá el desglose por forma de pago, operador y todos los movimientos registrados.</p></div></div>
                  <button type="button" className="secundario" onClick={abrirResumenSesion}><i className="pi pi-external-link" /> Ver resumen</button>
                </div>
              </>
            )}
          </section>
        )}

        {tab === "administracion" && (
          <section className="cajas-panel cajas-panel-tabla">
            <div className="cajas-panel-cabecera"><div><span className="cajas-seccion-icono"><i className="pi pi-desktop" /></span><div><small>Configuración</small><h2>Cajas disponibles</h2><p>{lista.length} {lista.length === 1 ? "caja configurada" : "cajas configuradas"}</p></div></div><button type="button" className="primario" onClick={() => abrirEdicion(null)}><i className="pi pi-plus" /> Nueva caja</button></div>
            {ocupado && !lista.length ? <div className="cajas-cargando"><i className="pi pi-spin pi-spinner" /> Cargando cajas...</div> : lista.length ? <TablaCajas lista={lista} accion={abrirEdicion} /> : <EstadoVacio icono="pi-desktop" titulo="No hay cajas configuradas" descripcion="Creá una caja para comenzar a registrar sesiones."><button type="button" className="primario" onClick={() => abrirEdicion(null)}><i className="pi pi-plus" /> Nueva caja</button></EstadoVacio>}
          </section>
        )}

        {tab === "historial" && (
          <>
            <form className="cajas-filtros" onSubmit={buscarHistorial}>
              <div className="cajas-filtros-titulo"><span className="cajas-seccion-icono"><i className="pi pi-filter" /></span><div><small>Filtros</small><strong>Historial de sesiones</strong></div></div>
              <label><span>Caja</span><select value={filtros.cajaId} onChange={(e) => setFiltros({ ...filtros, cajaId: e.target.value })}><option value="">Todas las cajas</option>{lista.map((caja) => <option key={caja.id} value={caja.id}>{caja.codigo} · {caja.descripcion}</option>)}</select></label>
              <label><span>Desde</span><input type="date" value={filtros.fechaDesde} onChange={(e) => setFiltros({ ...filtros, fechaDesde: e.target.value })} /></label>
              <label><span>Hasta</span><input type="date" value={filtros.fechaHasta} onChange={(e) => setFiltros({ ...filtros, fechaHasta: e.target.value })} /></label>
              <button className="primario" disabled={ocupado}><i className={`pi ${ocupado ? "pi-spin pi-spinner" : "pi-search"}`} /> {ocupado ? "Consultando..." : "Consultar"}</button>
            </form>
            <section className="cajas-panel cajas-panel-tabla">
              <div className="cajas-panel-cabecera">
                <div><span className="cajas-seccion-icono"><i className="pi pi-history" /></span><div><small>Resultados</small><h2>Sesiones de caja</h2><p>{historialConsultado ? `${historial.length} resultados` : "Seleccioná los filtros para consultar"}</p></div></div>
                {historialConsultado && <label className="cajas-filas">Filas <select value={paginacionHistorial.tamano} onChange={(evento) => setPaginacionHistorial({ pagina: 1, tamano: Number(evento.target.value) })}><option>25</option><option>50</option><option>100</option></select></label>}
              </div>
              {ocupado ? <div className="cajas-cargando"><i className="pi pi-spin pi-spinner" /> Buscando sesiones...</div> : historial.length ? <TablaHistorial lista={historialPaginado} ver={verDetalle} /> : <EstadoVacio icono={historialConsultado ? "pi-inbox" : "pi-search"} titulo={historialConsultado ? "No encontramos sesiones" : "Consultá el historial"} descripcion={historialConsultado ? "Probá ampliando el rango de fechas o seleccionando otra caja." : "Usá los filtros para buscar aperturas y cierres de caja."} />}
              {historial.length > 0 && <footer className="cajas-paginacion"><button type="button" disabled={paginaHistorial <= 1 || ocupado} onClick={() => setPaginacionHistorial((actual) => ({ ...actual, pagina: paginaHistorial - 1 }))} aria-label="Página anterior">‹</button><span>Página <strong>{paginaHistorial}</strong> de <strong>{paginasHistorial}</strong></span><button type="button" disabled={paginaHistorial >= paginasHistorial || ocupado} onClick={() => setPaginacionHistorial((actual) => ({ ...actual, pagina: paginaHistorial + 1 }))} aria-label="Página siguiente">›</button></footer>}
            </section>
          </>
        )}

        {tab === "formas" && (
          <section className="cajas-panel cajas-panel-tabla">
            <div className="cajas-panel-cabecera"><div><span className="cajas-seccion-icono"><i className="pi pi-credit-card" /></span><div><small>Configuración</small><h2>Formas de pago</h2><p>Definí cuáles se usan en caja y si impactan en el arqueo.</p></div></div></div>
            {formas.length ? (
              <div className="cajas-tabla"><table><thead><tr><th>Código</th><th>Descripción</th><th>Disponible</th><th>Impacta arqueo</th></tr></thead><tbody>{formas.map((forma) => <tr key={forma.formaPagoId}><td data-label="Código"><strong>{forma.codigo}</strong></td><td data-label="Descripción">{forma.descripcion}</td><td data-label="Disponible"><label className="cajas-switch"><input type="checkbox" checked={forma.activo} onChange={(e) => actualizarForma(forma, "activo", e.target.checked)} disabled={ocupado} /><span /> <b>{forma.activo ? "Activa" : "Inactiva"}</b></label></td><td data-label="Impacta arqueo"><label className="cajas-switch"><input type="checkbox" checked={forma.impactaArqueo} onChange={(e) => actualizarForma(forma, "impactaArqueo", e.target.checked)} disabled={ocupado} /><span /> <b>{forma.impactaArqueo ? "Sí" : "No"}</b></label></td></tr>)}</tbody></table></div>
            ) : <EstadoVacio icono="pi-credit-card" titulo="No hay formas de pago" descripcion="No se encontraron medios de pago configurados para cajas." />}
          </section>
        )}
      </div>

      {resumenModalAbierto && datosResumenModal && (
        <ResumenModal
          datos={datosResumenModal}
          movimientos={movimientosModal}
          onCerrar={() => { setResumenModalAbierto(false); setDetalle(null); }}
        />
      )}

      {!modoReporte && <CajaAperturaModal abierto={aperturaRequerida} onCerrar={() => useStoreCajas.setState({ aperturaRequerida: false })} />}
      {!modoReporte && modal && (
        <div className="cajas-modal-fondo" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setModal(false)}>
          <form className="cajas-modal" onSubmit={guardar}>
            <header><div className="cajas-modal-titulo"><span><i className={`pi ${item.id ? "pi-pencil" : "pi-plus"}`} /></span><div><small>Configuración</small><h2>{item.id ? "Editar" : "Nueva"} caja</h2></div></div><button type="button" className="cajas-modal-cerrar" aria-label="Cerrar" onClick={() => setModal(false)}><i className="pi pi-times" /></button></header>
            <div className="cajas-modal-cuerpo">
              <div className="cajas-modal-grid"><label><span>Código</span><input required value={item.codigo} onChange={(e) => setItem({ ...item, codigo: e.target.value })} placeholder="Ej. CAJA01" /></label><label><span>Descripción</span><input required value={item.descripcion} onChange={(e) => setItem({ ...item, descripcion: e.target.value })} placeholder="Ej. Caja principal" /></label></div>
              <label><span>Modo de operación</span><select value={item.modo} disabled={item.gestionAutomatica} onChange={(e) => setItem({ ...item, modo: e.target.value })}><option>Compartida</option><option>PorCajero</option></select><small className="cajas-ayuda">La gestión automática utiliza una sesión compartida continua.</small></label>
              <div className="cajas-modal-grid"><label><span>PC asignada <em>Opcional</em></span><input type="number" min="1" value={item.pcId || ""} onChange={(e) => setItem({ ...item, pcId: e.target.value || null })} placeholder="Sin asignar" /></label><label><span>Punto de venta <em>Opcional</em></span><select value={item.puntoVentaId || ""} onChange={(e) => setItem({ ...item, puntoVentaId: e.target.value || null })}><option value="">Sin asignar</option>{puntos.map((punto) => <option key={punto.id} value={punto.id}>{punto.sPuntoVenta || punto.descripcion}</option>)}</select></label></div>
              <label className="check cajas-check-tarjeta"><input type="checkbox" checked={item.gestionAutomatica} onChange={(e) => setItem({ ...item, gestionAutomatica: e.target.checked, modo: e.target.checked ? "Compartida" : item.modo, esPredeterminada: e.target.checked ? item.esPredeterminada : false })} /><span><strong>Gestión automática</strong><small>El sistema mantiene la caja abierta sin intervención del usuario.</small></span></label>
              <label className="check cajas-check-tarjeta"><input type="checkbox" checked={item.esPredeterminada} disabled={!item.gestionAutomatica || !item.activo} onChange={(e) => setItem({ ...item, esPredeterminada: e.target.checked })} /><span><strong>Caja predeterminada</strong><small>Se asigna automáticamente al vender desde una PC compatible.</small></span></label>
              <label className="check cajas-check-tarjeta"><input type="checkbox" checked={item.activo} onChange={(e) => setItem({ ...item, activo: e.target.checked, esPredeterminada: e.target.checked ? item.esPredeterminada : false })} /><span><strong>Caja activa</strong><small>Disponible para registrar movimientos.</small></span></label>
            </div>
            <footer><button type="button" className="secundario" onClick={() => setModal(false)}>Cancelar</button><button className="primario" disabled={ocupado}><i className={`pi ${ocupado ? "pi-spin pi-spinner" : "pi-check"}`} /> {ocupado ? "Guardando..." : "Guardar caja"}</button></footer>
          </form>
        </div>
      )}
    </main>
  );
}

function TablaCajas({ lista, accion }) {
  return (
    <div className="cajas-tabla"><table><thead><tr><th>Código</th><th>Descripción</th><th>Gestión</th><th>Predeterminada</th><th>PC</th><th>Punto de venta</th><th>Estado</th><th>Acciones</th></tr></thead><tbody>{lista.map((caja) => <tr key={caja.id}><td data-label="Código"><strong>{caja.codigo}</strong></td><td data-label="Descripción">{caja.descripcion}</td><td data-label="Gestión"><span className="cajas-dato-icono"><i className={`pi ${caja.gestionAutomatica ? "pi-bolt" : "pi-user"}`} /> {caja.gestionAutomatica ? "Automática" : "Manual"}</span></td><td data-label="Predeterminada">{caja.esPredeterminada ? "Sí" : "—"}</td><td data-label="PC">{caja.pcId || "—"}</td><td data-label="Punto de venta">{caja.puntoVenta || "—"}</td><td data-label="Estado"><Estado activo={caja.activo}>{caja.activo ? "Activa" : "Inactiva"}</Estado></td><td data-label="Acciones" className="cajas-acciones"><button type="button" className="secundario compacto" onClick={() => accion(caja)}><i className="pi pi-pencil" /> Editar</button></td></tr>)}</tbody></table></div>
  );
}

function TablaHistorial({ lista, ver }) {
  return (
    <div className="cajas-tabla"><table><thead><tr><th>Caja</th><th>Apertura</th><th>Cierre</th><th>Operadores</th><th className="numero">Inicial</th><th className="numero">Sistema</th><th className="numero">Declarado</th><th className="numero">Diferencia</th><th>Estado</th><th>Acciones</th></tr></thead><tbody>{lista.map((sesion) => <tr key={sesion.id}><td data-label="Caja"><strong>{sesion.cajaCodigo || sesion.cajaDescripcion}</strong></td><td data-label="Apertura">{formatearFechaCaja(sesion.fechaApertura)}</td><td data-label="Cierre">{formatearFechaCaja(sesion.fechaCierre)}</td><td data-label="Operadores">{(sesion.operadoresIds || []).join(" / ") || "—"}</td><td data-label="Inicial" className="numero">{dinero.format(sesion.saldoInicial || 0)}</td><td data-label="Sistema" className="numero">{dinero.format(sesion.saldoSistema || 0)}</td><td data-label="Declarado" className="numero">{sesion.saldoDeclarado == null ? "—" : dinero.format(sesion.saldoDeclarado)}</td><td data-label="Diferencia" className={`numero ${Number(sesion.diferencia) < 0 ? "negativo" : ""}`}>{sesion.diferencia == null ? "—" : dinero.format(sesion.diferencia)}</td><td data-label="Estado"><Estado activo={sesion.estado === "Abierta"}>{sesion.estado}</Estado></td><td data-label="Acciones" className="cajas-acciones"><button type="button" className="secundario compacto" onClick={() => ver(sesion.id)}><i className="pi pi-eye" /> Detalle</button></td></tr>)}</tbody></table></div>
  );
}

function ResumenModal({ datos, movimientos, onCerrar }) {
  useEffect(() => {
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const cerrarConEscape = (evento) => { if (evento.key === "Escape") onCerrar(); };
    document.addEventListener("keydown", cerrarConEscape);
    return () => {
      document.body.style.overflow = overflowAnterior;
      document.removeEventListener("keydown", cerrarConEscape);
    };
  }, [onCerrar]);

  const sesionDetalle = datos.sesion || {};
  return (
    <div className="cajas-resumen-modal-fondo" role="presentation" onMouseDown={(evento) => evento.target === evento.currentTarget && onCerrar()}>
      <section className="cajas-resumen-modal" role="dialog" aria-modal="true" aria-labelledby="cajas-resumen-modal-titulo">
        <header>
          <div className="cajas-resumen-modal-titulo">
            <span className="cajas-resumen-modal-icono"><i className="pi pi-chart-bar" /></span>
            <div><small>Actividad de caja</small><h2 id="cajas-resumen-modal-titulo">Resumen de sesión y movimientos</h2><p>{sesionDetalle.cajaCodigo ? `${sesionDetalle.cajaCodigo}${sesionDetalle.cajaDescripcion ? ` · ${sesionDetalle.cajaDescripcion}` : ""}` : "Detalle de la sesión seleccionada"}</p></div>
          </div>
          <div className="cajas-resumen-modal-acciones">
            {sesionDetalle.estado && <Estado activo={sesionDetalle.estado === "Abierta"}>{sesionDetalle.estado}</Estado>}
            <button type="button" className="cajas-resumen-modal-cerrar" aria-label="Cerrar resumen" onClick={onCerrar}><i className="pi pi-times" /></button>
          </div>
        </header>
        <div className="cajas-resumen-modal-cuerpo">
          <Resumen datos={datos} movimientos={movimientos} />
        </div>
      </section>
    </div>
  );
}

function Resumen({ datos, movimientos }) {
  if (!datos) return null;
  return (
    <section className="cajas-detalle">
      <div className="cajas-resumen-grid"><article><div><span>Saldo inicial</span><strong>{dinero.format(datos.sesion?.saldoInicial || 0)}</strong></div></article><article><div><span>Ingresos</span><strong className="positivo">{dinero.format(datos.ingresos || 0)}</strong></div></article><article><div><span>Egresos</span><strong>{dinero.format(datos.egresos || 0)}</strong></div></article><article className="destacada"><div><span>Saldo esperado</span><strong>{dinero.format(datos.saldoSistema || 0)}</strong></div></article></div>
      <div className="cajas-desglose-grid">
        <div><h3><i className="pi pi-credit-card" /> Por forma de pago</h3>{(datos.formasPago || []).length ? (datos.formasPago || []).map((forma) => <p key={forma.formaPagoId}><span>{forma.formaPago}</span><strong>{dinero.format(forma.total)}</strong></p>) : <span className="cajas-sin-datos">Sin movimientos registrados</span>}</div>
        <div><h3><i className="pi pi-users" /> Por operador</h3>{(datos.operadores || []).length ? (datos.operadores || []).map((operador) => <p key={operador.operadorId}><span>Operador #{operador.operadorId}<small>{operador.cantidadVentas} ventas</small></span><strong>{dinero.format(operador.total)}</strong></p>) : <span className="cajas-sin-datos">Sin actividad de operadores</span>}</div>
      </div>
      <div className="cajas-subtitulo-tabla"><div><h3>Movimientos</h3><span>{movimientos.length} registros</span></div></div>
      {movimientos.length ? <div className="cajas-tabla"><table><thead><tr><th>Fecha</th><th>Tipo</th><th>Forma de pago</th><th>Operador</th><th>Concepto</th><th className="numero">Importe</th></tr></thead><tbody>{movimientos.map((movimiento) => <tr key={movimiento.id}><td data-label="Fecha">{formatearFechaCaja(movimiento.fecha)}</td><td data-label="Tipo"><span className="cajas-tipo-movimiento">{movimiento.tipo}</span></td><td data-label="Forma de pago">{movimiento.formaPago}</td><td data-label="Operador">#{movimiento.operadorId}</td><td data-label="Concepto">{movimiento.concepto}</td><td data-label="Importe" className={`numero ${importeMovimientoVisual(movimiento) < 0 ? "negativo" : "positivo"}`}>{dinero.format(importeMovimientoVisual(movimiento))}</td></tr>)}</tbody></table></div> : <div className="cajas-tabla-vacia"><i className="pi pi-inbox" /><span>No hay movimientos en esta sesión.</span></div>}
    </section>
  );
}
